import { useFirestore, useFirestoreGeneralTwoOrderBy, useFirestoreOrderBy } from '../../firebase/useFirestore'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import { useContext, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import useSettings from "../../hooks/Settings";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import Presence from '../../components/personas/Dashboard/Presence';
import Absences from '../../components/personas/Absences.jsx';
import DeleteModal from '../../components/common/DeleteModal.jsx';
import { useTaskDTO } from '../../hooks/firebase/TaskDto.jsx';
import firebase from '../../firebase/compat';
import { RRule } from 'rrule';
import Table from '../../components/common/table/Table.jsx';
import TableCell from '../../components/common/table/TableCell.jsx';
import TableDragHandleCell from '../../components/common/table/TableDragHandleCell.jsx';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';

const Shedule = ({ personaNavigation, item, courseStartDate, courseEndDate }) => {
  // Context
  const [, setSaved] = useContext(SavedIcon)
  const [auth] = useContext(Auth)

  // State
  const [dragArray, setDragArray] = useState([]);

  // Hooks
  const impactFunctionality = useSettings().impactFunctionality
  const taskDTO = useTaskDTO();
  const history = useHistory();

  // Firestore
  const activities = useFirestoreOrderBy('Activities', 'Position', 'asc');
  const outputs = useFirestoreOrderBy('Outputs', 'Position', 'asc');
  const locations = useFirestore('Locations');
  const scheduleTasks = useFirestoreGeneralTwoOrderBy(
    'Tasks',
    'PersonaID',
    item?.ID || '',
    'Type',
    'personaSchedule',
    'Position',
    'asc'
  );

  // Keep local reorder array in sync
  useEffect(() => {
    setDragArray(Array.isArray(scheduleTasks) ? scheduleTasks : []);
  }, [scheduleTasks]);

  // Dutch weekdays + mapping to RRule BYDAY
  const DUTCH_WEEKDAYS = useMemo(
    () => ([
      { key: 'monday', label: 'Maandag', rrule: RRule.MO, jsDay: 1 },
      { key: 'tuesday', label: 'Dinsdag', rrule: RRule.TU, jsDay: 2 },
      { key: 'wednesday', label: 'Woensdag', rrule: RRule.WE, jsDay: 3 },
      { key: 'thursday', label: 'Donderdag', rrule: RRule.TH, jsDay: 4 },
      { key: 'friday', label: 'Vrijdag', rrule: RRule.FR, jsDay: 5 },
      { key: 'saturday', label: 'Zaterdag', rrule: RRule.SA, jsDay: 6 },
      { key: 'sunday', label: 'Zondag', rrule: RRule.SU, jsDay: 0 },
    ]),
    []
  );

  const getWeekdayConfig = (key) => DUTCH_WEEKDAYS.find(d => d.key === key) || DUTCH_WEEKDAYS[0];

  const parseTimeToHM = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return { h: 9, m: 0 };
    const [hStr, mStr] = timeStr.split(':');
    const h = Number(hStr);
    const m = Number(mStr);
    return {
      h: Number.isFinite(h) ? h : 9,
      m: Number.isFinite(m) ? m : 0,
    };
  };

  const nextDateForWeekday = (targetJsDay, h, m) => {
    const now = new Date();
    const base = new Date(now);
    base.setHours(h, m, 0, 0);

    const currentDay = base.getDay();
    let diff = targetJsDay - currentDay;

    // If same weekday but time already passed, move to next week
    if (diff === 0 && base.getTime() <= now.getTime()) diff = 7;
    if (diff < 0) diff += 7;

    base.setDate(base.getDate() + diff);
    return base;
  };

  const buildWeeklyRRule = ({ weekdayKey, startTime }) => {
    const dayCfg = getWeekdayConfig(weekdayKey);
    const { h, m } = parseTimeToHM(startTime);

    const dtstart = nextDateForWeekday(dayCfg.jsDay, h, m);

    const rule = new RRule({
      freq: RRule.WEEKLY,
      interval: 1,
      byweekday: [dayCfg.rrule],
      dtstart,
    });

    return {
      rruleString: rule.toString(),
      dtstart,
    };
  };

  const dateInputToTimestamp = (value) => {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return firebase.firestore.Timestamp.fromDate(new Date(y, m - 1, d));
  };

  const timestampToDateInput = (ts) => {
    if (!ts || typeof ts.toDate !== 'function') return '';
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const todayMidnightTimestamp = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return firebase.firestore.Timestamp.fromDate(t);
  };

  const updateLocal = (docid, updates) => {
    setDragArray((prev) =>
      prev.map((t) => (t.docid === docid ? { ...t, ...updates } : t))
    );
  };

  const updateTask = async (docid, updates) => {
    if (!docid) return;
    await taskDTO.update(docid, updates);
    setSaved('flex');
  };

  // DRAG & DROP: reorder items and update Firestore
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((t, idx) => {
      if (!t?.docid) return;
      db.collection('Tasks').doc(t.docid).update({ Position: idx + 1 });
    });

    setSaved('flex');
  };

  // Welke roosterregel er verwijderd gaat worden; null = geen venster open.
  const [rowToDelete, setRowToDelete] = useState(/** @type {object | null} */ (null));

  /**
   * Vraagt om bevestiging voordat een roosterregel verdwijnt.
   *
   * Hier werd de regel meteen weggehaald, zonder iets te vragen — en die
   * regels staan óók in de agenda, dus daar verdween zonder waarschuwing een
   * blok. Updateverzoek BuurtKlusBedrijf.
   *
   * `currentTarget` en niet `target`: het icoon van MUI tekent een <svg> met
   * <path> erin, en een klik landt op dat pad. Dat kind draagt de docid niet.
   * AI-CONTEXT: See docs/pitfalls.md#state-event-target-vs-currenttarget
   *
   * @param {React.MouseEvent<HTMLElement>} e
   * @returns {void}
   */
  const askDeleteTask = (e) => {
    const docid = e.currentTarget.dataset.docid;
    if (!docid) return;
    setRowToDelete(scheduleTasks?.find?.(row => row.docid === docid) || { docid });
  };

  /**
   * Verwijdert de roosterregel waarvoor zojuist bevestigd is.
   *
   * Zacht verwijderen (ADR-008), met de auth-id erbij zodat in de prullenbak
   * staat wie het deed.
   *
   * @returns {Promise<void>}
   */
  const deleteTask = async () => {
    if (!rowToDelete?.docid) return;
    await taskDTO.remove(rowToDelete.docid, auth?.ID);
    setRowToDelete(null);
    setSaved('flex');
  };

  /**
   * De naam van de regel in het bevestigingsvenster. Een roosterregel heeft
   * geen titel, dus de dag en de tijden zijn waaraan je hem herkent.
   *
   * @param {object|null} row - De roosterregel
   * @returns {string}
   */
  const scheduleRowName = (row) => {
    if (!row) return 'deze roosterregel';
    const dag = DUTCH_WEEKDAYS.find(d => d.key === (row.Day || 'monday'))?.label || 'Onbekende dag';
    return `de roosterregel van ${dag.toLowerCase()} ${row.StartTime || '09:00'}–${row.EndTime || '17:00'}`;
  };

  // Update weekday
  const updateWeekday = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const weekdayKey = e.currentTarget.value;

    const row = dragArray.find(d => d.docid === docid);
    const startTime = row?.StartTime || '09:00';
    const endTime = row?.EndTime || '17:00';

    updateLocal(docid, { Day: weekdayKey });

    const { rruleString, dtstart } = buildWeeklyRRule({ weekdayKey, startTime });

    const { h: eh, m: em } = parseTimeToHM(endTime);
    const dtend = new Date(dtstart);
    dtend.setHours(eh, em, 0, 0);

    await updateTask(docid, {
      Day: weekdayKey,
      rrule: rruleString,
      Timestamp: firebase.firestore.Timestamp.fromDate(dtstart),
      TimestampEnd: firebase.firestore.Timestamp.fromDate(dtend),
    });
  };

  // Update start and endtime
  const startTimeHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const startTime = e.currentTarget.value;

    const row = dragArray.find(d => d.docid === docid);
    const weekdayKey = row?.Day || 'monday';
    const endTime = row?.EndTime || '17:00';

    updateLocal(docid, { StartTime: startTime });

    const { rruleString, dtstart } = buildWeeklyRRule({ weekdayKey, startTime });

    const { h: eh, m: em } = parseTimeToHM(endTime);
    const dtend = new Date(dtstart);
    dtend.setHours(eh, em, 0, 0);

    await updateTask(docid, {
      StartTime: startTime,
      rrule: rruleString,
      Timestamp: firebase.firestore.Timestamp.fromDate(dtstart),
      TimestampEnd: firebase.firestore.Timestamp.fromDate(dtend),
    });
  };

  const endTimeHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const endTime = e.currentTarget.value;

    const row = dragArray.find(d => d.docid === docid);
    const weekdayKey = row?.Day || 'monday';
    const startTime = row?.StartTime || '09:00';

    updateLocal(docid, { EndTime: endTime });

    const { rruleString, dtstart } = buildWeeklyRRule({ weekdayKey, startTime });

    const { h: eh, m: em } = parseTimeToHM(endTime);
    const dtend = new Date(dtstart);
    dtend.setHours(eh, em, 0, 0);

    await updateTask(docid, {
      EndTime: endTime,
      rrule: rruleString,
      Timestamp: firebase.firestore.Timestamp.fromDate(dtstart),
      TimestampEnd: firebase.firestore.Timestamp.fromDate(dtend),
    });
  };

  // Add schedule row (Task)
  const addDay = async () => {
    if (!item?.ID || !auth?.ID) return;

    const weekdayKey = 'monday';
    const startTime = '09:00';
    const endTime = '17:00';

    const { rruleString, dtstart } = buildWeeklyRRule({ weekdayKey, startTime });

    const { h: eh, m: em } = parseTimeToHM(endTime);
    const dtend = new Date(dtstart);
    dtend.setHours(eh, em, 0, 0);

    const newTask = taskDTO.newDoc({
      startDate: dtstart,
      endDate: dtend,
      userId: auth.ID,
      companyId: client,
      PersonaID: item.ID,
      Type: 'personaSchedule',
    });

    // UI/helper fields + ordering
    const personaLabel = item?.Name || item?.Title || item?.FirstName || item?.DisplayName || '';

    await taskDTO.create({
      ...newTask,
      Title: personaLabel,
      Description: personaLabel,
      Position: (dragArray?.length || 0) + 1,

      Day: weekdayKey,
      StartTime: startTime,
      EndTime: endTime,
      LocationID: item?.LocationID || '',

      EffectiveFrom: todayMidnightTimestamp(),
      EffectiveUntil: null,

      rrule: rruleString,
      exdate: [],
      exrule: null,
    });

    setSaved('flex');
  };

  const effectiveFromHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const ts = dateInputToTimestamp(e.currentTarget.value);
    updateLocal(docid, { EffectiveFrom: ts });
    // Ook een leeggemaakte datum (ts === null) moet weg naar de Task: anders
    // blijft de oude datum in de database staan terwijl het scherm leeg is, en
    // blijft de nachtklus die outputs genereert deze roosterregel overslaan.
    await updateTask(docid, { EffectiveFrom: ts });
  };

  const effectiveUntilHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const ts = dateInputToTimestamp(e.currentTarget.value);
    updateLocal(docid, { EffectiveUntil: ts });
    await updateTask(docid, { EffectiveUntil: ts });
  };

  // Location handler
  const locationHandler = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const locationID = e.currentTarget.value;

    if (locationID === 'create') {
      history.push(`/${client}/settings/6`);
      return;
    }

    updateLocal(docid, { LocationID: locationID });

    await updateTask(docid, { LocationID: locationID });
  }

  // Output handler
  const outputHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const outputID = e.target.value;

    updateLocal(docid, { OutputID: outputID });

    await updateTask(docid, { OutputID: outputID });
  }

  const scheduleColumns = [
    { key: 'drag', label: '', searchable: false },
    { key: 'day', label: 'DAG' },
    { key: 'start', label: 'START', searchable: false },
    { key: 'end', label: 'EIND', searchable: false },
    { key: 'location', label: 'LOCATIE', searchable: false },
    { key: 'effectiveFrom', label: 'VANAF', searchable: false },
    { key: 'effectiveUntil', label: 'T/M', searchable: false },
    ...(impactFunctionality ? [{ key: 'output', label: 'HOOFD-OUTPUT', searchable: false }] : []),
    { key: 'delete', label: 'VERWIJDEREN', searchable: false },
  ];

  return (
    <div className={`persona-presence-panel${personaNavigation === 'presence' ? '' : ' is-hidden'}`}>
      <div className='table-container dashboard-container'>
        <h2>Rooster</h2>

        <ReadOnlyGuard edit="personas.edit">
          {(canEdit) => (
            <Table
              columns={scheduleColumns}
              items={dragArray || []}
              getItemKey={(row) => row.docid}
              renderRow={(row) => (
                <>
                  <TableDragHandleCell>
                    <DragIndicatorOutlinedIcon />
                  </TableDragHandleCell>

                  <TableCell>
                    <select
                      data-docid={row.docid}
                      value={row.Day || 'monday'}
                      onChange={updateWeekday}
                    >
                      {DUTCH_WEEKDAYS.map(d => (
                        <option key={d.key} value={d.key}>{d.label}</option>
                      ))}
                    </select>
                  </TableCell>

                  <TableCell>
                    <input
                      type="time"
                      value={row.StartTime || '09:00'}
                      data-docid={row.docid}
                      onChange={startTimeHandler}
                    />
                  </TableCell>

                  <TableCell>
                    <input
                      type="time"
                      value={row.EndTime || '17:00'}
                      data-docid={row.docid}
                      onChange={endTimeHandler}
                    />
                  </TableCell>

                  <TableCell>
                    <select
                      data-docid={row.docid}
                      value={row.LocationID || ''}
                      onChange={locationHandler}
                    >
                      <option value=''>-- Geen locatie --</option>
                      {locations && locations.map(location => (
                        <option key={location.docid} value={location.docid}>{location.Title}</option>
                      ))}
                      <option value='create'>-- Creeer nieuwe locatie --</option>
                    </select>
                  </TableCell>

                  <TableCell>
                    <input
                      type="date"
                      data-docid={row.docid}
                      value={timestampToDateInput(row.EffectiveFrom)}
                      onChange={effectiveFromHandler}
                    />
                  </TableCell>

                  <TableCell>
                    <input
                      type="date"
                      data-docid={row.docid}
                      value={timestampToDateInput(row.EffectiveUntil)}
                      onChange={effectiveUntilHandler}
                      placeholder="Onbepaald"
                    />
                  </TableCell>

                  {impactFunctionality && (
                    <TableCell>
                      <select
                        data-docid={row.docid}
                        value={row.OutputID || ''}
                        onChange={outputHandler}
                      >
                        <option value=''>-- Geen output --</option>
                        {activities && activities.map(act => {
                          const actOutputs = outputs ? outputs.filter(o => o.ActivityID === act.ID) : [];
                          if (actOutputs.length === 0) return null;
                          return (
                            <optgroup key={act.ID} label={act.Activity}>
                              {actOutputs.map(out => (
                                <option key={out.ID} value={out.ID}>{out.Title} ({act.Activity})</option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                    </TableCell>
                  )}

                  <TableCell>
                    {canEdit && (
                      <DeleteOutlinedIcon
                        className='table-delete-icon'
                        data-docid={row.docid}
                        onClick={askDeleteTask}
                      />
                    )}
                  </TableCell>
                </>
              )}
              searchAccessor={(row) => getWeekdayConfig(row?.Day || 'monday').label}
              searchPlaceholder="Zoeken op dag"
              emptyMessage="Nog geen roosterdagen toegevoegd."
              draggable
              droppableId="personaSchedule"
              onDragEnd={handleDragEnd}
              addRow={canEdit ? {
                toolTipContent: 'Dag toevoegen',
                onClick: addDay,
              } : undefined}
            />
          )}
        </ReadOnlyGuard>
      </div>

      <Absences item={item} />

      <DeleteModal
        deleteModal={rowToDelete !== null}
        setDeleteModal={(open) => { if (!open) setRowToDelete(null); }}
        deleteItem={deleteTask}
        deleteName={scheduleRowName(rowToDelete)}
        softDelete
      />

      <Presence item={item} courseStartDate={courseStartDate} courseEndDate={courseEndDate} />
    </div>
  );
};

export default Shedule;
